/**
 * Shared local styled primitives for the first-run setup wizard's step
 * content (Welcome/AdminAccount/EnrollMFA/RecoveryKey/ServiceDefaults/Finish).
 *
 * OIDCPAM Gate-6 Phase 2b: there is no published @devnw/forms or
 * @devnw/primitives single-line text-input/heading/button component yet
 * (same gap noted in Phase 2a's WebAuthnCredentials migration — see
 * gate6-phase2a-report.md's concern #2), so these steps build minimal
 * themed elements directly from `@devnw/theme`'s `asUnified`/`ut` helpers —
 * mirroring the pattern already used by `pages/WebAuthnCredentials.tsx`'s
 * AliasField* styled components and by `@devnw/cyber-iam/auth`'s own
 * internal `styledShared` (which is NOT a public import: its module
 * comment states "NOT a public API ... only the component subpaths are
 * supported", so this file re-derives the same look from public theme
 * tokens instead of reaching into that internal module).
 */
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'

/** Centers the wizard shell on the page with themed padding — the page-level wrapper. */
export const WizardPage = styled.main`
  display: flex;
  justify-content: center;
  padding: ${({ theme }) => ut(asUnified(theme)).space('xl')};
`

export const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('md')};
  max-width: 480px;
  width: 100%;
`

export const StepContainerWide = styled(StepContainer)`
  max-width: 560px;
`

export const StepHeading = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => asUnified(theme).font.sizes.lg};
  font-weight: 600;
  color: ${({ theme }) => asUnified(theme).text.primary};
`

export const StepDescription = styled.p`
  margin: 0;
  font-size: ${({ theme }) => asUnified(theme).font.sizes.sm};
  color: ${({ theme }) => asUnified(theme).text.secondary};
`

export const ErrorAlert = styled.div`
  align-self: stretch;
  border: 1px solid ${({ theme }) => asUnified(theme).state?.danger ?? '#991b1b'};
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius()}px;
  padding: ${({ theme }) => ut(asUnified(theme)).space('sm')};
  font-size: ${({ theme }) => asUnified(theme).font.sizes.sm};
  color: ${({ theme }) => asUnified(theme).state?.danger ?? '#991b1b'};
  word-break: break-word;
`

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('xxs')};
`

export const FieldLabel = styled.label`
  font-size: ${({ theme }) => asUnified(theme).font.sizes.sm};
  font-weight: 600;
  color: ${({ theme }) => asUnified(theme).text.primary};
`

export const FieldInput = styled.input`
  min-height: 44px;
  padding: ${({ theme }) =>
    `${ut(asUnified(theme)).space('xs')} ${ut(asUnified(theme)).space('sm')}`};
  border: 1px solid ${({ theme }) => ut(asUnified(theme)).borderColor()};
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius()}px;
  font-size: ${({ theme }) => asUnified(theme).font.sizes.sm};
  color: ${({ theme }) => asUnified(theme).text.primary};
  background: ${({ theme }) => asUnified(theme).bg.canvas};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => asUnified(theme).colors?.primary};
    outline-offset: 1px;
  }

  &:disabled {
    opacity: 0.5;
  }
`

export const FieldHelperText = styled.span`
  font-size: ${({ theme }) => asUnified(theme).font.sizes.xs};
  color: ${({ theme }) => asUnified(theme).text.secondary};
`

export const ButtonRow = styled.div`
  display: flex;
  gap: ${({ theme }) => ut(asUnified(theme)).space('sm')};
`

export const PrimaryButton = styled.button`
  min-height: 44px;
  padding: ${({ theme }) =>
    `${ut(asUnified(theme)).space('xs')} ${ut(asUnified(theme)).space('md')}`};
  border: none;
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius()}px;
  background: ${({ theme }) => asUnified(theme).colors?.primary};
  color: ${({ theme }) => asUnified(theme).text.inverse};
  font-size: ${({ theme }) => asUnified(theme).font.sizes.sm};
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => asUnified(theme).colors?.primary};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const SecondaryButton = styled.button`
  min-height: 44px;
  padding: ${({ theme }) =>
    `${ut(asUnified(theme)).space('xs')} ${ut(asUnified(theme)).space('md')}`};
  border: 1px solid ${({ theme }) => ut(asUnified(theme)).borderColor()};
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius()}px;
  background: none;
  color: ${({ theme }) => asUnified(theme).text.primary};
  font-size: ${({ theme }) => asUnified(theme).font.sizes.sm};
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => asUnified(theme).bg.canvas};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => asUnified(theme).colors?.primary};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const DefaultsList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('sm')};
`

export const DefaultsListItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('xxs')};
  padding: ${({ theme }) => ut(asUnified(theme)).space('sm')};
  border: 1px solid ${({ theme }) => ut(asUnified(theme)).borderColor()};
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius()}px;
  background: ${({ theme }) => asUnified(theme).bg.surface};
`

export const DefaultsItemTitle = styled.span`
  font-size: ${({ theme }) => asUnified(theme).font.sizes.sm};
  font-weight: 600;
  color: ${({ theme }) => asUnified(theme).text.primary};
`

export const DefaultsItemBody = styled.span`
  font-size: ${({ theme }) => asUnified(theme).font.sizes.xs};
  color: ${({ theme }) => asUnified(theme).text.secondary};
`
